import { bible_highlight, bible_verse_highlight, type PowerSyncDrizzleDB } from "@bso/powersync";
import { patchRowsVerified, patchRowVerified } from "../powersync/verified-write";

/**
 * UPDATE writes on `bible_highlight`/`bible_verse_highlight` that the
 * user usually triggers seconds after CREATING the highlight (choosing
 * color on the fly, undo, delete and restore from trash) — the window
 * exact ghost UPDATE trap against the PowerSync view (see
 * `../powersync/verified-write.ts`). Extracted from `apps/web/src/routes/_app/
 * highlights.tsx` and `profile.trash.tsx` to be the SAME function exercised
 * by production and tests — never a parallel reimplementation.
 */

/** Soft delete of a highlight group: flat rows + origin group. */
export async function softDeleteHighlightMutation(
  db: PowerSyncDrizzleDB,
  rowIds: readonly string[],
  originId: string | null,
): Promise<void> {
  const now = new Date().toISOString();
  if (rowIds.length > 0) {
    await patchRowsVerified(db, bible_verse_highlight, bible_verse_highlight.id, rowIds, {
      deleted_at: now,
      updated_at: now,
    });
  }
  if (originId) {
    await patchRowVerified(db, bible_highlight, bible_highlight.id, originId, {
      deleted_at: now,
      updated_at: now,
    });
  }
}

/** Inversa exacta de {@link softDeleteHighlightMutation} (undo / papelera). */
export async function restoreHighlightMutation(
  db: PowerSyncDrizzleDB,
  rowIds: readonly string[],
  originId: string | null,
): Promise<void> {
  const now = new Date().toISOString();
  if (rowIds.length > 0) {
    await patchRowsVerified(db, bible_verse_highlight, bible_verse_highlight.id, rowIds, {
      deleted_at: null,
      updated_at: now,
    });
  }
  if (originId) {
    await patchRowVerified(db, bible_highlight, bible_highlight.id, originId, {
      deleted_at: null,
      updated_at: now,
    });
  }
}

/** Changes the color of the origin GROUP (editing from `/highlights`). */
export async function updateHighlightGroupColorMutation(
  db: PowerSyncDrizzleDB,
  originId: string,
  color: string,
): Promise<void> {
  await patchRowVerified(db, bible_highlight, bible_highlight.id, originId, {
    color,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Adopts orphan flat rows (created before groups existed)
 * under a newly inserted origin group.
 */
export async function attachHighlightOriginMutation(
  db: PowerSyncDrizzleDB,
  rowIds: readonly string[],
  originId: string,
): Promise<void> {
  await patchRowsVerified(db, bible_verse_highlight, bible_verse_highlight.id, rowIds, {
    origin_id: originId,
    updated_at: new Date().toISOString(),
  });
}

/** Keeps flat row color (reader) synchronized with the group. */
export async function updateHighlightRowsColorMutation(
  db: PowerSyncDrizzleDB,
  rowIds: readonly string[],
  color: string,
): Promise<void> {
  await patchRowsVerified(db, bible_verse_highlight, bible_verse_highlight.id, rowIds, {
    color,
    updated_at: new Date().toISOString(),
  });
}
